import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import {
  CustomAutoComplete,
  CustomTextField,
  DecimalTextField,
  DialogActions,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  InfoTooltip,
  NoDataSkeleton,
  renderTwoLineOption,
  RowTableCell,
} from '../../../common';
import {
  AUTOCOMPLETE_LISTBOX_PROPS,
  TABLE_INPUT_CELL_SX,
  TABLE_INPUT_SX,
} from '../../../common/styles';
import {
  buildIssuePayloadFromItemRows,
  formatSupplyOrderQuantity,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';
import { guardDialogClose } from '../../utils/actionLockUtils';
import {
  SUPPLY_ORDER_HELP_TEXTS,
  SUPPLY_ORDER_ITEM_TABLE_TEXTS as ITEM_TEXTS,
  SUPPLY_ORDER_PRICE_TEXTS,
} from '../constants/supplyOrdersConstants';
import { toProviderOptions } from '../../utils/toProviderOptions';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
const MIN_TABLE_WIDTH = 920;
const textSx = { textAlign: 'left' };
const numberSx = { textAlign: 'right' };
const inputCellSx = TABLE_INPUT_CELL_SX;
const PRICE_INPUT_PROPS = { style: { textAlign: 'right' } };

const COLUMNS = [
  { label: ITEM_TEXTS.article, sx: textSx },
  { label: ITEM_TEXTS.quantity, sx: { ...numberSx, width: 120 } },
  { label: ITEM_TEXTS.provider, sx: { ...textSx, width: 272 } },
  {
    label: SUPPLY_ORDER_PRICE_TEXTS.header,
    title: SUPPLY_ORDER_PRICE_TEXTS.tooltip,
    sx: { ...numberSx, width: 172 },
  },
  { label: ITEM_TEXTS.commitment, sx: { ...textSx, width: 192 } },
];

const IssueSupplyOrderDialog = ({
  open,
  onClose,
  order,
  providers = [],
  onIssue,
  isProcessing = false,
}) => {
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const orderItems = useMemo(() => order?.items || [], [order?.items]);
  const providerOptions = useMemo(
    () => toProviderOptions(providers),
    [providers]
  );

  useEffect(() => {
    if (open && order) {
      setRows(
        orderItems.map((item) => ({
          uuid: item.uuid,
          unit_price: item.unit_price ?? '',
          article_code: item.article_code_snapshot,
          article_name: item.article_name_snapshot,
          origin: item.origin,
          requested_quantity: item.requested_quantity,
          uuid_provider_legal_entity:
            item.uuid_provider_legal_entity || order.uuid_provider_legal_entity || '',
          provider_delivery_commitment_at: (item.provider_delivery_commitment_at
            || order.provider_delivery_commitment_at || '').split('T')[0],
        }))
      );
      setErrorMessage('');
    }
  }, [open, order, orderItems]);

  const handleRowChange = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleIssue = async () => {
    const hasInvalidPrice = rows.some(
      (row) =>
        row.unit_price === '' ||
        !Number.isFinite(Number(row.unit_price)) ||
        Number(row.unit_price) < 0
    );
    if (hasInvalidPrice) {
      setErrorMessage('Informá un precio unitario válido en pesos para cada artículo.');
      return;
    }
    const missingProvider = rows.some((row) => !row.uuid_provider_legal_entity);
    if (missingProvider) {
      setErrorMessage('Seleccioná un proveedor para cada artículo.');
      return;
    }

    const payload = buildIssuePayloadFromItemRows(rows);
    if (!payload) {
      setErrorMessage('No hay artículos para emitir.');
      return;
    }

    try {
      setErrorMessage('');
      await onIssue(payload);
      onClose();
    } catch (error) {
      setErrorMessage(
        getSupplyOrderApiErrorMessage(error, 'No se pudo emitir la orden.')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer
      open={open}
      onClose={guardDialogClose(isProcessing, onClose)}
      size="large"
      minHeight="50vh"
    >
      <DialogTitle>Emitir orden a proveedor</DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Artículos
            </Typography>
            <InfoTooltip lines={[SUPPLY_ORDER_HELP_TEXTS.issue]} />
          </Box>

          <ClientPaginatedTable rows={rows} resetKey={order?.uuid}>
            {(pageRows, pageStart) => (
              <GeneralTableBox hasMaxHeight={false} fixedLayout minTableWidth={MIN_TABLE_WIDTH}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((column) => (
                        <HeadTableCell key={column.label} sx={column.sx} title={column.title}>
                          {column.label}
                        </HeadTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <NoDataSkeleton
                        colSpan={COLUMNS.length}
                        message={ITEM_TEXTS.empty}
                        showSubtitle={false}
                      />
                    ) : (
                      pageRows.map((row, index) => (
                        <TableRow key={row.uuid} hover>
                          <RowTableCell sx={textSx} title={row.article_name}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, lineHeight: 1.25 }}
                              noWrap
                            >
                              {row.article_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                              {row.article_code}
                            </Typography>
                            {row.origin && (
                              <Typography variant="caption" component="div" title={row.origin.label}>
                                {row.origin.order_number} · {row.origin.label}
                              </Typography>
                            )}
                          </RowTableCell>
                          <RowTableCell sx={numberSx}>
                            {formatSupplyOrderQuantity(row.requested_quantity)}
                          </RowTableCell>
                          <RowTableCell sx={{ ...textSx, ...inputCellSx }}>
                            <CustomAutoComplete
                              name="uuid_provider_legal_entity"
                              value={row.uuid_provider_legal_entity}
                              options={providerOptions}
                              renderOption={renderTwoLineOption}
                              inputPlaceholder={ITEM_TEXTS.searchProvider}
                              ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
                              onChange={(event) =>
                                handleRowChange(
                                  pageStart + index,
                                  'uuid_provider_legal_entity',
                                  event.target.value
                                )
                              }
                              size="small"
                              sx={TABLE_INPUT_SX}
                            />
                          </RowTableCell>
                          <RowTableCell sx={{ ...numberSx, ...inputCellSx }}>
                            <DecimalTextField
                              decimals={2}
                              value={row.unit_price}
                              onChange={(event) =>
                                handleRowChange(pageStart + index, 'unit_price', event.target.value)
                              }
                              inputProps={PRICE_INPUT_PROPS}
                              size="small"
                              sx={TABLE_INPUT_SX}
                            />
                          </RowTableCell>
                          <RowTableCell sx={{ ...textSx, ...inputCellSx }}>
                            <CustomTextField
                              type="date"
                              value={row.provider_delivery_commitment_at}
                              onChange={(event) =>
                                handleRowChange(
                                  pageStart + index,
                                  'provider_delivery_commitment_at',
                                  event.target.value
                                )
                              }
                              shrinkLabel
                              size="small"
                              sx={TABLE_INPUT_SX}
                            />
                          </RowTableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </GeneralTableBox>
            )}
          </ClientPaginatedTable>
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleIssue}
        saveLabel="Emitir orden"
        saveButtonProps={{ isLoading: isProcessing }}
      />
    </DialogContainer>
  );
};

export default IssueSupplyOrderDialog;
